import type { PageImageOutcomeReporter } from 'features/knowledgeDocumentViewer/hooks/usePageImageOutcome'
import type { PageImageRecovery } from 'features/knowledgeDocumentViewer/hooks/usePageImageRecovery'
import {
  type PageImageFailureReason,
  type PageImageSource,
  resolvePageImageFailure,
} from 'features/knowledgeDocumentViewer/telemetry/pageImageOutcome'
import { useCallback, useEffect, useMemo, useState } from 'react'

export type UsePageImageFailureArgs = {
  imageKey: string
  rolodexUrl: string | undefined
  recovery: PageImageRecovery
  outcome: PageImageOutcomeReporter
  pdf: { error: boolean; reload: () => void }
  pdfAttempted: boolean
  hasDisplayUrl: boolean
  isLoading: boolean
  rolodexMissed: boolean
  lookupUnavailable: boolean
  isCurrentPage: boolean
}

export type PageImageFailureState = {
  failed: boolean
  reason: PageImageFailureReason
  source: PageImageSource | undefined
  onImageError: (src: string) => void
  retry: () => void
}

/**
 * The failure shell shared by the continuous and paged viewers: remembers a
 * broken PDF.js render per image, decides when a page has run out of
 * sources, names the failure for telemetry and reports it once, and offers
 * a retry that re-signs the Rolodex source and re-renders the PDF page.
 */
export function usePageImageFailure({
  imageKey,
  rolodexUrl,
  recovery,
  outcome,
  pdf,
  pdfAttempted,
  hasDisplayUrl,
  isLoading,
  rolodexMissed,
  lookupUnavailable,
  isCurrentPage,
}: UsePageImageFailureArgs): PageImageFailureState {
  const [brokenPdfKey, setBrokenPdfKey] = useState<string | null>(null)
  const pdfBroken = brokenPdfKey === imageKey
  const { error: pdfError, reload: reloadPdf } = pdf

  const failed =
    isCurrentPage &&
    (pdfBroken || (!hasDisplayUrl && !isLoading && rolodexMissed))
  const { reason, source } = resolvePageImageFailure({
    exhausted: recovery.exhausted,
    pdfBroken,
    pdfError,
    lookupUnavailable,
    pdfAttempted,
  })

  useEffect(() => {
    if (failed) outcome.reportFailed(reason, source)
  }, [failed, outcome, reason, source])

  const onImageError = useCallback(
    (src: string) => {
      recovery.onError(src)
      if (src !== rolodexUrl) setBrokenPdfKey(imageKey)
    },
    [imageKey, recovery, rolodexUrl]
  )

  const retry = useCallback(() => {
    setBrokenPdfKey(null)
    recovery.retry()
    reloadPdf()
  }, [recovery, reloadPdf])

  return useMemo(
    () => ({ failed, reason, source, onImageError, retry }),
    [failed, reason, source, onImageError, retry]
  )
}
